import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const Services = () => {
  const serviceCategories = [
    {
      category: 'Commercial EPC Services (South West UK)',
      services: [
        {
          id: 'commercial_warehouse',
          title: 'Commercial EPC - Warehouses & Industrial',
          price: 'From £180',
          description: 'Non-domestic EPCs for warehouses, factories, logistics centres and industrial units across Exeter, Devon, Cornwall, Somerset and the South West.',
          details: '£180 for up to 5 rooms, £30 per additional room',
          location: 'South West UK',
          featured: true
        },
        {
          id: 'commercial_hotel_office',
          title: 'Commercial EPC - Hotels & Offices',
          price: 'From £180',
          description: 'Commercial EPCs for hotels, offices, retail units and other non-domestic buildings across the South West.',
          details: '£180 for up to 150m², £0.80 per additional m²',
          location: 'South West UK'
        },
        {
          id: 'commercial_advice',
          title: 'Commercial Energy Consultancy',
          price: 'From £300',
          description: 'Detailed energy advice for commercial property owners and landlords across the South West, including MEES compliance guidance.',
          details: '£300 for up to 5 rooms, £30 per additional room. Includes non-domestic MEES advice for landlords.',
          location: 'South West UK'
        }
      ]
    },
    {
      category: 'Nationwide Digital Services',
      services: [
        {
          id: 'sap_calculation',
          title: 'SAP Calculations',
          price: 'From £180',
          description: 'Standard Assessment Procedure calculations for new builds, extensions and conversions. Essential for Building Regulations Part L compliance.',
          details: '£180 base, £30 per additional room. Extras: Repeat unit £65, EPC lodgement £65, Extension SAP £120, Part O £150',
          location: 'Nationwide'
        },
        {
          id: 'u_values',
          title: 'U-Value Calculations',
          price: 'From £65',
          description: 'Thermal performance calculations for building elements to meet Building Regulations.',
          details: 'Wall: £65, Floor: £65, Roof: £65',
          location: 'Nationwide',
          linkOverride: '/u-value-calculations'
        },
        {
          id: 'part_o',
          title: 'Part O Overheating Calculations',
          price: 'From £150',
          description: 'Building Regulations Part O overheating risk assessments for new residential buildings in England.',
          details: 'Simplified Part O: £150 per dwelling. Dynamic thermal modelling available for complex projects.',
          location: 'Nationwide',
          featured: true
        },
        {
          id: 'sbem',
          title: 'SBEM Calculations',
          price: 'Prices on Request',
          description: 'Simplified Building Energy Model calculations for non-domestic Building Regulations Part L compliance.',
          details: 'Typical range £300-£2,000+ depending on building complexity. Contact us for a tailored quote.',
          location: 'Nationwide'
        },
        {
          id: 'thermal_bridge',
          title: 'Thermal Bridge Ψ-Value Calculations',
          price: 'Price on Request',
          description: 'Bespoke thermal bridge (Ψ-value) calculations for junctions in new builds and extensions. Reduces heat loss and condensation risk for Part L compliance.',
          details: 'Bespoke Ψ-value calculations, ACD reviews and junction assessments. Contact us for a quote.',
          location: 'Nationwide'
        },
        {
          id: 'dynamic_thermal',
          title: 'Dynamic Thermal Modelling',
          price: 'Price on Request',
          description: 'Hour-by-hour thermal modelling to assess overheating risk and internal comfort. Supports Part O compliance and CIBSE TM52/TM59 assessments.',
          details: 'Overheating assessments, design optimisation and Building Regulations support. Contact us for a quote.',
          location: 'Nationwide'
        },
        {
          id: 'retrofit',
          title: 'Retrofit Coordination (PAS 2035)',
          price: 'Price on Request',
          description: 'PAS 2035 Retrofit Coordinator and whole-house assessments. Structured, compliant retrofit planning for domestic properties across Devon and the South West.',
          details: 'Whole-house plans, risk assessments and coordination between assessors, designers and installers. Contact us for a quote.',
          location: 'Devon & South West'
        },
        {
          id: 'heat_load_remote',
          title: 'Remote Heat Load Calculation',
          price: 'From £145',
          description: 'Independent, MCS-compliant heat load calculations from your floor plans and drawings. No installer affiliation — your report is yours to use with any contractor.',
          details: '£145 base (up to 5 rooms), £25 per additional room. Full MCS Design Pack from £295.',
          location: 'Nationwide',
          linkOverride: '/independent-heat-load-calculations',
          featured: true
        },
        {
          id: 'heat_load_onsite',
          title: 'On-Site Heat Load Survey',
          price: 'From £195',
          description: 'Independent on-site heat load survey with physical inspection of construction elements, insulation and glazing. MCS MIS 3005-D compliant.',
          details: '£195 base (up to 5 rooms), £30 per additional room. EPC + Heat Load bundle from £245.',
          location: 'Devon & South West',
          linkOverride: '/independent-heat-load-calculations'
        }
      ]
    },
    {
      category: 'Domestic Services',
      services: [
        {
          id: 'domestic_epc',
          title: 'Domestic Property EPC',
          price: 'From £120',
          description: 'Energy Performance Certificates for residential properties in Exeter and the surrounding area. Legally required when selling or letting domestic properties.',
          details: '£120 for up to 5 rooms, £5 per additional room',
          location: 'Exeter'
        },
        {
          id: 'domestic_advice',
          title: 'Domestic Energy Advice Report',
          price: 'From £180',
          description: 'Comprehensive energy advice across the South West UK including EPC and MEES compliance support for landlords.',
          details: '£180 for up to 5 rooms, £5 per additional room (includes EPC). MEES advice for rental properties.',
          location: 'South West UK'
        }
      ]
    },
    {
      category: 'Floor Plans',
      services: [
        {
          id: 'leasehold_plans',
          title: 'Leasehold / To-Scale Plans',
          price: 'From £120',
          description: 'Accurate to-scale plans for leasehold properties and legal requirements. DIY photo-based option at HousePlanDirect.com.',
          details: '£120 for up to 5 rooms, £5 per additional room. Elevations: £65. DIY alternative: HousePlanDirect.com from £30 - create plans from existing plan photos',
          location: 'Exeter'
        },
        {
          id: 'marketing_plans',
          title: 'Marketing Floor Plans',
          price: 'From £85',
          description: 'High-quality floor plans for property marketing and sales. DIY option available at HousePlanDirect.com from £30.',
          details: '£85 for up to 5 rooms, £5 per additional room. 3D upgrade available. Budget DIY option: HousePlanDirect.com converts old plans from phone photos for just £30',
          location: 'Exeter'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Energy Services Exeter & South West | EPCs, SAP, U-Values, Floor Plans"
        description="Professional energy assessment services in Exeter and South West England. Domestic & commercial EPCs, SAP calculations, U-values, energy consultancy, floor plans, and MEES advice. Book online."
        keywords="EPC services Exeter, SAP calculations, U-value assessments, energy consultancy South West, floor plans Exeter, MEES compliance"
        canonical="/services"
      />
      <Breadcrumbs items={[
        { name: 'Services', path: '/services' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white" data-testid="services-heading">
              Our Services
            </h1>
            <p className="text-base sm:text-lg opacity-95 text-white">
              Comprehensive energy assessment and calculation services for domestic, commercial, and nationwide projects.
            </p>
          </div>
        </div>
      </section>

      {/* Top Services Quick Links */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Links to Our Services</h2>
            <div className="flex flex-wrap gap-3">
              <Link to="/services/commercial_warehouse" className="text-sm text-green-600 hover:text-green-700 font-medium">Commercial EPC →</Link>
              <Link to="/services/commercial_advice" className="text-sm text-green-600 hover:text-green-700 font-medium">Energy Consultancy →</Link>
              <Link to="/sap-calculations" className="text-sm text-green-600 hover:text-green-700 font-medium">SAP Calculations →</Link>
              <Link to="/lease-plans" className="text-sm text-green-600 hover:text-green-700 font-medium">Lease Plans →</Link>
              <Link to="/services/domestic_epc" className="text-sm text-green-600 hover:text-green-700 font-medium">Domestic EPC →</Link>
              <Link to="/services/marketing_plans" className="text-sm text-green-600 hover:text-green-700 font-medium">Floor Plans →</Link>
              <Link to="/u-value-calculations" className="text-sm text-green-600 hover:text-green-700 font-medium">U-Value Calculations →</Link>
              <Link to="/services/retrofit" className="text-sm text-green-600 hover:text-green-700 font-medium">Retrofit Coordinator →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {serviceCategories.map((category, catIndex) => (
            <div key={catIndex} className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">{category.category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.services.map((service, index) => (
                  <Card 
                    key={service.id} 
                    className="border-none shadow-md hover:shadow-xl transition-all card-hover overflow-hidden"
                    data-testid={`service-card-${service.id}`}
                  >
                    <div className="h-2 bg-gradient-to-r from-green-600 to-cyan-500"></div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <MapPin className="w-4 h-4" />
                        <span>{service.location}</span>
                      </div>
                      <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                      <p className="text-3xl font-bold text-green-600 mb-4">{service.price}</p>
                      <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                      <p className="text-xs text-gray-500 mb-4 italic">{service.details}</p>
                      <Link to={service.linkOverride || `/services/${service.id}`}>
                        <Button 
                          data-testid={`service-details-btn-${service.id}`}
                          className="w-full text-white"
                          style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                        >
                          View Details
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HousePlanDirect.com Section */}
      <section className="py-12 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold text-gray-900 mb-3">Budget-Friendly Floor Plan Option</h3>
                  <p className="text-xl text-blue-600 font-semibold mb-2">HousePlanDirect.com</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Perfect For:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>Converting old floor plans using phone photos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>Quick turnaround needed</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>Budget-conscious projects</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>DIY property documentation</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">How It Works:</h4>
                    <ol className="space-y-2 text-gray-700">
                      <li className="flex gap-2">
                        <span className="font-bold text-blue-600">1.</span>
                        <span>Take photos of your existing floor plans</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-bold text-blue-600">2.</span>
                        <span>Upload via phone or computer</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-bold text-blue-600">3.</span>
                        <span>Receive digital floor plans quickly</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-bold text-blue-600">4.</span>
                        <span>Download and use for your needs</span>
                      </li>
                    </ol>
                  </div>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Starting from</p>
                    <p className="text-3xl font-bold text-blue-600">£30</p>
                  </div>
                  <a 
                    href="https://www.houseplandirect.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold transition-colors shadow-md hover:shadow-lg"
                  >
                    Visit HousePlanDirect.com
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Travel Surcharge Info */}
      <section className="py-12 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Travel Surcharge Information</h3>
            <p className="text-gray-700 mb-4">
              Properties within 1 hour of Exeter are included at standard pricing.
            </p>
            <p className="text-gray-700 font-medium">
              Travel surcharge: £25 per hour for travel beyond 1 hour.
            </p>
            <p className="text-sm text-gray-600 mt-4">
              Nationwide digital services (SAP, U-Values, SBEM, etc.) have no travel surcharge.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Book?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Get your energy assessment or calculation started today.
            </p>
            <Link to="/booking">
              <Button 
                data-testid="services-book-now-btn"
                size="lg" 
                className="text-white px-12 py-6 text-lg rounded-full shadow-lg"
                style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
              >
                Book Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;